import React, { useState, useEffect, useRef } from 'react';
import { ShoppingCart, X, Trash2, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { useOptionsBasket, isMarketOpen } from './OptionsBasketContext';
import './OptionsBasketPanel.css';

// The actual basket UI (legs, margins, place) — shared by OptionsBasketDrawer
// (renders this inside a centered overlay, reachable from any chain page) and
// the dedicated /order-basket page (renders this inline, full-width). Kept
// as one component so both stay in sync automatically instead of drifting.
// `onClose`, if given, renders a Close button next to Place All (the overlay
// usage); the full-page usage omits it since there's nothing to "close".
const OptionsBasketPanel = ({ onClose }) => {
    const {
        basketItems, removeFromBasket, updateItem, clearBasket, placeAll,
        isPlacing, placeResults, prices, registerPanelMounted, registerPanelUnmounted,
    } = useOptionsBasket();

    // Tells the context to start/stop polling `prices` — polling is keyed off
    // "is any panel actually visible", not off the overlay drawer specifically,
    // since this same component also renders inline on the /order-basket page.
    useEffect(() => {
        registerPanelMounted();
        return () => registerPanelUnmounted();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Nothing is checked by default — placing is opt-in per leg, so a leg
    // just added doesn't accidentally go out with the next basket-wide place.
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    useEffect(() => {
        setSelectedIds(prev => {
            const validIds = new Set(basketItems.map(i => i.id));
            const next = new Set();
            prev.forEach(id => { if (validIds.has(id)) next.add(id); });
            return next;
        });
    }, [basketItems]);

    const toggleSelected = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const allSelected = basketItems.length > 0 && basketItems.every(i => selectedIds.has(i.id));
    const toggleSelectAll = () => {
        setSelectedIds(allSelected ? new Set() : new Set(basketItems.map(i => i.id)));
    };

    // Up/Down moves a keyboard "focused" row through the basket; Enter
    // toggles that row's checkbox — lets a fast desk select legs without
    // reaching for the mouse. Ignored while typing in a Lots/Price/SL input
    // so arrow keys there still behave like normal number-input steppers.
    const [focusedIdx, setFocusedIdx] = useState(0);
    useEffect(() => {
        setFocusedIdx(i => Math.min(i, Math.max(0, basketItems.length - 1)));
    }, [basketItems.length]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (basketItems.length === 0) return;
            const tag = document.activeElement?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA') return;
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setFocusedIdx(i => Math.min(basketItems.length - 1, i + 1));
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setFocusedIdx(i => Math.max(0, i - 1));
            } else if (e.key === 'Enter') {
                e.preventDefault();
                const item = basketItems[focusedIdx];
                if (item) toggleSelected(item.id);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [basketItems, focusedIdx]);

    const firstQtyInputRef = useRef(null);

    useEffect(() => {
        if (basketItems.length > 0) {
            const frame = requestAnimationFrame(() => firstQtyInputRef.current?.focus());
            return () => cancelAnimationFrame(frame);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Flash a leg's LTP green/up or red/down for half a second on an actual
    // change — motion reads as "live" far faster than a value comparison.
    const [flash, setFlash] = useState({});
    const prevPricesRef = useRef({});
    useEffect(() => {
        const changed = {};
        for (const sym in prices) {
            const prev = prevPricesRef.current[sym];
            if (prev !== undefined && prices[sym] !== prev) {
                changed[sym] = prices[sym] > prev ? 'up' : 'down';
            }
        }
        prevPricesRef.current = prices;
        if (Object.keys(changed).length === 0) return;
        setFlash((f) => ({ ...f, ...changed }));
        const timer = setTimeout(() => {
            setFlash((f) => {
                const next = { ...f };
                for (const sym in changed) delete next[sym];
                return next;
            });
        }, 500);
        return () => clearTimeout(timer);
    }, [prices]);

    const marketOpen = isMarketOpen();

    const handlePlace = () => placeAll(selectedIds);

    // Ctrl/Cmd+Enter fires the (checked) basket — works the same way whether
    // this panel is shown inside the floating drawer or inline on the
    // dedicated /order-basket page, since the listener lives here rather
    // than in either wrapper.
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                if (selectedIds.size > 0 && !isPlacing && marketOpen) handlePlace();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIds, isPlacing, marketOpen]);

    return (
        <div className="opt-basket-panel">
            <div className="opt-panel-header">
                <div className="opt-panel-title">
                    <ShoppingCart size={17} className="opt-panel-title-icon" />
                    <span>Basket</span>
                    <span className="opt-basket-count">{basketItems.length}</span>
                </div>
                <div className="opt-panel-header-actions">
                    {onClose && <button className="opt-panel-close" onClick={onClose}><X size={16} /></button>}
                </div>
            </div>

            <div className="opt-panel-body">
                {basketItems.length === 0 ? (
                    <div className="opt-basket-empty">
                        <div className="opt-basket-empty-icon-ring"><ShoppingCart size={22} strokeWidth={1.5} /></div>
                        <p>Basket is empty</p>
                        <span>Click a strike's price on the option chain to add a leg</span>
                    </div>
                ) : (
                    <div className="opt-leg-list">
                        <div className="opt-leg-row opt-leg-header-row">
                            <label className="opt-leg-col-check">
                                <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                            </label>
                            <span className="opt-leg-col-type" />
                            <span className="opt-leg-col-instrument">Instrument</span>
                            <span className="opt-leg-col-ltp">LTP</span>
                            <span className="opt-leg-col-spacer" />
                            <span className="opt-leg-col-toggles">Order</span>
                            <span className="opt-leg-col-qty">Lots</span>
                            <span className="opt-leg-col-spacer" />
                            <span className="opt-leg-col-price">Price</span>
                            <span className="opt-leg-col-spacer" />
                            <span className="opt-leg-col-sl">SL pts</span>
                            <span className="opt-leg-col-spacer" />
                            <span className="opt-leg-col-remove" />
                        </div>
                        {basketItems.map((item, idx) => {
                            const ltp = prices[item.tradingsymbol];
                            const qty = (item.lots || 0) * item.lot_size;
                            const strikeTypeSuffix = `${item.strike}${item.instrument_type}`;
                            const symbolName = item.tradingsymbol.endsWith(strikeTypeSuffix)
                                ? item.tradingsymbol.slice(0, item.tradingsymbol.length - strikeTypeSuffix.length)
                                : item.tradingsymbol;
                            return (
                                <div
                                    key={item.id}
                                    className={`opt-leg-row opt-leg-${item.instrument_type.toLowerCase()} ${idx === focusedIdx ? 'opt-leg-row-focused' : ''}`}
                                    onClick={() => { setFocusedIdx(idx); toggleSelected(item.id); }}
                                >
                                    <label className="opt-leg-col-check" onClick={(e) => e.stopPropagation()}>
                                        <input type="checkbox" checked={selectedIds.has(item.id)} onChange={() => toggleSelected(item.id)} />
                                    </label>

                                    <div className="opt-segment-pill" onClick={(e) => e.stopPropagation()}>
                                        <button
                                            className={`opt-segment-option ${item.transaction_type === 'BUY' ? 'active-buy' : ''}`}
                                            onClick={() => updateItem(item.id, 'transaction_type', 'BUY')}
                                        >Buy</button>
                                        <button
                                            className={`opt-segment-option ${item.transaction_type === 'SELL' ? 'active-sell' : ''}`}
                                            onClick={() => updateItem(item.id, 'transaction_type', 'SELL')}
                                        >Sell</button>
                                    </div>

                                    <div className="opt-leg-instrument">
                                        <strong>
                                            <span className="opt-leg-name-part">{symbolName}</span>{' '}
                                            <span className="opt-leg-strike-part">{item.strike}</span>{' '}
                                            <span className="opt-leg-type-part">{item.instrument_type}</span>
                                        </strong>
                                    </div>

                                    <span className={`opt-leg-ltp ${flash[item.tradingsymbol] ? `flash-${flash[item.tradingsymbol]}` : ''}`}>
                                        {ltp !== undefined ? ltp : '…'}
                                    </span>

                                    <span className="opt-leg-col-spacer" />

                                    <div className="opt-segment-pill" onClick={(e) => e.stopPropagation()}>
                                        <button
                                            className={`opt-segment-option ${item.order_type === 'MARKET' ? 'active' : ''}`}
                                            onClick={() => updateItem(item.id, 'order_type', 'MARKET')}
                                        >MARKET</button>
                                        <button
                                            className={`opt-segment-option ${item.order_type === 'LIMIT' ? 'active' : ''}`}
                                            onClick={() => {
                                                updateItem(item.id, 'order_type', 'LIMIT');
                                                if (!item.price && ltp !== undefined) {
                                                    updateItem(item.id, 'price', ltp);
                                                }
                                            }}
                                        >LIMIT</button>
                                    </div>

                                    <label className="opt-leg-col-qty" title={`${item.lots || 0} lot(s) × ${item.lot_size} = ${qty} qty`} onClick={(e) => e.stopPropagation()}>
                                        <input
                                            ref={idx === 0 ? firstQtyInputRef : undefined}
                                            type="number"
                                            className="opt-leg-mini-input"
                                            min="1"
                                            step="1"
                                            value={item.lots}
                                            onChange={(e) => updateItem(item.id, 'lots', e.target.value)}
                                        />
                                    </label>

                                    <span className="opt-leg-col-spacer" />

                                    <span className="opt-leg-col-price" onClick={(e) => e.stopPropagation()}>
                                        {item.order_type === 'LIMIT' ? (
                                            <input
                                                type="number"
                                                className="opt-leg-mini-input"
                                                step="0.05"
                                                value={item.price || ''}
                                                onChange={(e) => updateItem(item.id, 'price', e.target.value)}
                                            />
                                        ) : '—'}
                                    </span>

                                    <span className="opt-leg-col-spacer" />

                                    <span className="opt-leg-col-sl" title="Fixed server-side">{item.sl_points || '—'}</span>

                                    <span className="opt-leg-col-spacer" />

                                    <button
                                        className="opt-basket-remove-btn opt-leg-col-remove"
                                        onClick={(e) => { e.stopPropagation(); removeFromBasket(item.id); }}
                                    >
                                        Remove
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}

                {placeResults && (
                    <div className="opt-basket-results">
                        {placeResults.results && placeResults.results.length > 0 && (
                            <div className="opt-results-section success">
                                <h4><CheckCircle2 size={13} /> Placed</h4>
                                {placeResults.results.map((r, idx) => (
                                    <div key={idx} className="opt-result-row success">
                                        <CheckCircle2 size={14} className="opt-result-icon" />
                                        <div className="opt-result-body">
                                            <strong>{r.symbol}</strong>
                                            <span>Entry #{r.entry_order_id}{r.entry_price != null ? ` @ ${r.entry_price}` : ' — pending fill'}</span>
                                            {r.sl_order_id ? (
                                                <span>SL #{r.sl_order_id} @ {r.sl_trigger_price}</span>
                                            ) : r.note ? (
                                                <span className="opt-result-note">{r.note}</span>
                                            ) : null}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {placeResults.errors && placeResults.errors.length > 0 && (
                            <div className="opt-results-section error">
                                <h4><XCircle size={13} /> Failed</h4>
                                {placeResults.errors.map((r, idx) => (
                                    <div key={idx} className="opt-result-row error">
                                        <XCircle size={14} className="opt-result-icon" />
                                        <div className="opt-result-body">
                                            <strong>{r.symbol}</strong>
                                            <span>{r.error}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {basketItems.length > 0 && (
                <div className="opt-panel-footer">
                    <div className="opt-panel-footer-bottom">
                        <span className="opt-panel-kbd-hint">
                            <kbd>{navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}</kbd>+<kbd>⏎</kbd> place{onClose ? ' all' : ''}
                            {onClose && <> · <kbd>Esc</kbd> close</>}
                        </span>
                        {onClose && <button className="opt-panel-close-btn" onClick={onClose}>Close</button>}
                        <button className="opt-basket-place-btn" onClick={handlePlace} disabled={isPlacing || selectedIds.size === 0 || !marketOpen} title={!marketOpen ? "Market is closed — option orders can't be placed until 09:15 IST" : undefined}>
                            {isPlacing ? <Loader2 size={15} className="opt-spin" /> : <ShoppingCart size={14} />}
                            {isPlacing ? 'Placing…' : !marketOpen ? 'Market Closed' : `Place ${selectedIds.size} Leg${selectedIds.size !== 1 ? 's' : ''}`}
                        </button>
                        <button className="opt-panel-clear-link" onClick={clearBasket}>
                            <Trash2 size={12} /> Clear basket
                        </button>
                    </div>
                </div>
            )}

        </div>
    );
};

export default OptionsBasketPanel;
